import styled from '@emotion/styled';
import { useState, useRef } from 'react';

// Components
import { proCatData } from './ProCatData';
import ProCatForm from './ProCatForm';
import ProCatResults from './ProCatResults';

const CalculatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CalcFormContainer = styled.div`
  background: #e6e6e6;
  padding: 15px;
  width: 100%;

  @media (min-width: 768px) {
    width: 33.333%;
  }
`;

const CalcResultsContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 66.666%;
  }
`;

// --- interpolation helpers ---
const R13_BASE = { thickness: proCatData.R13.thickness, bags: proCatData.R13.numberOfBags }; // 4.75", 5.1

const rows = Object.values(proCatData)
  .map(({ thickness, numberOfBags }) => ({ thickness, bags: numberOfBags }))
  .sort((a, b) => a.thickness - b.thickness);

function interpolateBags(thicknessNeeded) {
  // exact match first
  const exact = rows.find(r => r.thickness === thicknessNeeded);
  if (exact) return exact.bags;

  // below minimum: scale from R13
  if (thicknessNeeded > 0 && thicknessNeeded < rows[0].thickness) {
    return (thicknessNeeded / R13_BASE.thickness) * R13_BASE.bags;
  }

  // between two rows: linear interpolation
  for (let i = 1; i < rows.length; i += 1) {
    const lo = rows[i - 1];
    const hi = rows[i];
    if (thicknessNeeded > lo.thickness && thicknessNeeded < hi.thickness) {
      const ratio = (thicknessNeeded - lo.thickness) / (hi.thickness - lo.thickness);
      return lo.bags + ratio * (hi.bags - lo.bags);
    }
  }

  // above our max row: use the top row (or extend data later)
  return rows[rows.length - 1].bags;
}

// eslint-disable-next-line react/prop-types
const ProCatCalculator = ({ costPerBag, installers, laborRate, markup, footnotes }) => {
  const [input, setInput] = useState({
    rvalue: 'R49',
    costPerBag,
    installers,
    laborCost: laborRate,
    markup,

    atticSqFt: '',
    currentDepth: '',
    sqFtError: null,
    currentDepthError: null,
  });
  const [proCatInfo, setProCatInfo] = useState({});
  const scrollToDiv = useRef();

  const scrollToResults = () => {
    if (!scrollToDiv.current) return;
    const headerOffset = 110;
    const y = scrollToDiv.current.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const setValuesToZero = (targetInsulationDepth, message) => {
    setProCatInfo({
      targetInsulationDepth,
      insulationNeeded: 0,
      bagsNeeded: 0,
      blowTime: 0,
      totalMaterialCost: 0,
      totalLaborCost: 0,
      totalCost: 0,
      customerCost: 0,
      margin: 0,
    });

    if (message) {
      setInput(s => ({
        ...s,
        sqFtError: null,
        currentDepthError: message,
      }));
    } else {
      setInput(s => ({ ...s, sqFtError: null, currentDepthError: null }));
    }

    scrollToResults();
    return true;
  };

  const inputChange = e => {
    const { name, value } = e.target;

    setInput(prevState => ({ ...prevState, [name]: value }));
  };

  const formSubmit = e => {
    e.preventDefault();

    // clear any old errors
    setInput(s => ({ ...s, sqFtError: null, currentDepthError: null }));

    const sqft = parseFloat(input.atticSqFt);
    const currentDepth = parseFloat(input.currentDepth);
    const parsedInstallers = parseFloat(input.installers);
    const parsedCostPerBag = parseFloat(input.costPerBag);
    const laborPerHour = parseFloat(input.laborCost);
    const markupPct = parseFloat(input.markup);

    // basic validation
    if (!Number.isFinite(sqft) || sqft <= 0) {
      setInput(s => ({ ...s, sqFtError: "Please enter your attic's square ft", currentDepthError: null }));
      return;
    }
    if (!Number.isFinite(currentDepth) || currentDepth < 0) {
      setInput(s => ({ ...s, currentDepthError: 'Current depth must be 0 or more', sqFtError: null }));
      return;
    }

    const targetInsulationDepth = proCatData[input.rvalue].thickness;
    const rawNeeded = targetInsulationDepth - currentDepth;
    const insulationNeeded = Math.round(rawNeeded * 100) / 100; // 2 decimals
    // get bags per 1000 using exact match / interpolation / sub-4.75 scaling
    const bagsPer1000 = interpolateBags(insulationNeeded);
    // scale by area
    const numberOfBags = Math.round(bagsPer1000 * (sqft / 1000));

    if (numberOfBags <= 0) {
      if (setValuesToZero(targetInsulationDepth)) return;
    }

    if (rawNeeded <= 0) {
      if (
        setValuesToZero(
          targetInsulationDepth,
          // eslint-disable-next-line max-len
          `Existing depth (${currentDepth}") is at/above target (${targetInsulationDepth}"). No additional insulation needed.`
        )
      )
        return;
    }

    // time & costs
    const setUpTimeHours = 1.5;
    const minutesPerBag = 7;
    const blowTime = (numberOfBags * minutesPerBag) / 60;
    const crewTimeHours = parseFloat((blowTime + setUpTimeHours).toFixed(1));
    const totalCrewHours = crewTimeHours * parsedInstallers;

    const materialCostRaw = numberOfBags * parsedCostPerBag;
    const laborCostRaw = totalCrewHours * laborPerHour;
    const totalCostRaw = materialCostRaw + laborCostRaw;

    const markupFraction = (Number.isFinite(markupPct) ? markupPct : 0) / 100;
    const customerCostRaw = totalCostRaw * (1 + markupFraction);
    const marginRaw = customerCostRaw - totalCostRaw;

    const totalMaterialCost = Math.round(materialCostRaw);
    const totalLaborCost = Math.round(laborCostRaw);
    const totalCost = Math.round(totalCostRaw);
    const customerCost = Math.round(customerCostRaw);
    const margin = Math.round(marginRaw);

    setProCatInfo({
      targetInsulationDepth,
      insulationNeeded,
      bagsNeeded: numberOfBags,
      blowTime,
      totalMaterialCost,
      totalLaborCost,
      totalCost,
      customerCost,
      margin,
    });

    scrollToResults();
  };

  return (
    <CalculatorContainer>
      <CalcFormContainer>
        <ProCatForm inputChange={inputChange} input={input} formSubmit={formSubmit} />
      </CalcFormContainer>

      <CalcResultsContainer ref={scrollToDiv}>
        <ProCatResults
          targetInsulationDepth={proCatInfo.targetInsulationDepth}
          insulationNeeded={proCatInfo.insulationNeeded}
          bagsNeeded={proCatInfo.bagsNeeded}
          blowTime={proCatInfo.blowTime}
          totalMaterialCost={proCatInfo.totalMaterialCost}
          totalLaborCost={proCatInfo.totalLaborCost}
          totalCost={proCatInfo.totalCost}
          customerCost={proCatInfo.customerCost}
          margin={proCatInfo.margin}
          footnotes={footnotes}
        />
      </CalcResultsContainer>
    </CalculatorContainer>
  );
};

export default ProCatCalculator;
